import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getPaymentType } from '@/api/clarityLens/salesLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { formatMetric } from '@/hooks/clarity/util'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import { PaymentTypeItemResponseModel } from '@/types/clarityLens'

type ChartDataModel = {
  data: Record<string, number>
  labels: string[]
}

type LineChartDataModel = {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}

const PAYMENT_TYPE_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'Payment Type',
    accessorKey: 'paymentType',
    type: 'text',
  },
  {
    header: 'Amount',
    accessorKey: 'amount',
    type: 'money',
  },
  {
    header: 'Tip Amount',
    accessorKey: 'tipAmount',
    type: 'money',
  },
]

export default function usePaymentType(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemGroupName?.trim()),
    [startDate, endDate, branches, itemGroupName]
  )
  const loading = !isReady || isFetching

  const [paymentTypeData, setPaymentTypeData] =
    useState<(PaymentTypeItemResponseModel & { paymentType: string })[]>()
  const [
    paymentMethodDistributionChartData,
    setPaymentMethodDistributionChartData,
  ] = useState<ChartDataModel>()
  const [paymentTypeChartData, setPaymentTypeChartData] =
    useState<ChartDataModel>()
  const [driveThruVsInStoreChartData, setDriveThruVsInStoreChartData] =
    useState<ChartDataModel>()
  const [driveThruVsInStoreLineChartData, setDriveThruVsInStoreLineChartData] =
    useState<LineChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const {
        tableData,
        paymentTypeChartData,
        paymentMethodDistributionChartData,
        driveThruVsInStoreChartData,
        driveThruVsInStoreLineChartData,
      } = await fetchPaymentTypeData(
        branches,
        startDate,
        endDate,
        itemGroupName,
        sizeFilter,
        temperatureFilter
      )

      setPaymentTypeData(tableData)
      setPaymentTypeChartData(paymentTypeChartData)
      setPaymentMethodDistributionChartData(paymentMethodDistributionChartData)
      setDriveThruVsInStoreChartData(driveThruVsInStoreChartData)
      setDriveThruVsInStoreLineChartData(driveThruVsInStoreLineChartData)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [
    isReady,
    branches,
    startDate,
    endDate,
    itemGroupName,
    sizeFilter,
    temperatureFilter,
  ])

  const refetch = useCallback(() => {
    console.log('Refetching discounts items...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    paymentTypeData,
    PAYMENT_TYPE_COLUMNS,
    paymentTypeChartData,
    paymentMethodDistributionChartData,
    driveThruVsInStoreChartData,
    driveThruVsInStoreLineChartData,
    loading,
    refetch,
  }
}

async function fetchPaymentTypeData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
): Promise<{
  tableData: (PaymentTypeItemResponseModel & { paymentType: string })[]
  paymentTypeChartData: ChartDataModel
  paymentMethodDistributionChartData: ChartDataModel
  driveThruVsInStoreChartData: ChartDataModel
  driveThruVsInStoreLineChartData: LineChartDataModel
}> {
  try {
    const response = await getPaymentType(
      branches,
      startDate,
      endDate,
      itemGroupName,
      sizeFilter,
      temperatureFilter
    )

    const tableData = Object.entries(response).map(([keyName, value]) => {
      return convertToPaymentTypeResponseModel(
        keyName,
        value as unknown as PaymentTypeItemResponseModel
      )
    })

    const paymentTypeChartData: ChartDataModel = {
      data: {
        Credit: response.CREDIT.count,
        Cash: response.CASH.count,
        Giftcard: response.GIFTCARD.count,
        Other: response.OTHER.count,
      },
      labels: ['Credit', 'Cash', 'Giftcard', 'Other'],
    }

    // TODO: data is real, make it dynamic when API is updated
    const driveThruVsInStoreChartData: ChartDataModel = {
      data: {
        'Drive-Thru': 100,
        'In-Store': 0,
      },
      labels: ['Drive-Thru', 'In-Store'],
    }

    // Generate daily dates for the selected date range
    const dailyDates = generateDailyDates(startDate, endDate)

    // TODO: data is real, make it dynamic when API is updated
    // For now, using hardcoded values: 100 for Drive-Thru, 0 for In-Store for each day
    const driveThruVsInStoreLineChartData: LineChartDataModel = {
      rawData: [
        dailyDates.map(() => 100), // Drive-Thru values
        dailyDates.map(() => 0), // In-Store values
      ],
      seriesNames: ['Drive-Thru', 'In-Store'],
      xAxisNames: dailyDates,
    }

    // TODO: data is real, make it dynamic when API is updated
    const paymentMethodDistributionChartData: ChartDataModel = {
      data: {
        'Online Pick-up': 0,
        'In-Store': 100,
        Delivery: 0,
      },
      labels: ['Online Pick-up', 'In-Store', 'Delivery'],
    }

    return {
      tableData,
      paymentTypeChartData,
      driveThruVsInStoreChartData,
      driveThruVsInStoreLineChartData,
      paymentMethodDistributionChartData,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertToPaymentTypeResponseModel(
  keyName: string,
  value: PaymentTypeItemResponseModel
): PaymentTypeItemResponseModel & { paymentType: string } {
  const { count, amount, tipAmount } = value

  const baseModel: PaymentTypeItemResponseModel & { paymentType: string } = {
    paymentType: keyName,
    count: formatMetric(count, 0),
    amount: formatMetric(amount, 2),
    tipAmount: formatMetric(tipAmount, 2),
  }

  return baseModel
}

// Generate array of daily dates between startDate and endDate
function generateDailyDates(startDate: string, endDate: string): string[] {
  const dates: string[] = []
  const start = new Date(startDate)
  const end = new Date(endDate)

  // Format date as MM/DD
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${month}/${day}`
  }

  const currentDate = new Date(start)
  while (currentDate <= end) {
    dates.push(formatDate(currentDate))
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return dates
}
